import { createRouter, createWebHistory, RouteLocationNormalized } from 'vue-router';
import { analytics, perf } from '@/firebase';
import { Analytics, logEvent } from "firebase/analytics";

import NotFound from '@/views/NotFound.vue';
import Maintenance from '@/views/Maintenance.vue';
import Error from '@/views/Error.vue';

import History from '@/views/History/History.vue';
import HistoryDetails from '@/views/History/HistoryDetails.vue';
import HistoryCreate from '@/views/History/HistoryCreate.vue';
import MergeTrip from '@/views/History/MergeTrip.vue';
import TransactionsOverview from '@/views/History/TransactionsOverview.vue';

import VoucherList from '@/views/Vouchers/VoucherList.vue';
import VoucherCustomAdd from '@/views/Vouchers/VoucherCustomAdd.vue';

import Loyalty from '@/views/Loyalty/Loyalty.vue';
import LoyaltyAdd from '@/views/Loyalty/LoyaltyAdd.vue';
import LoyaltyRegister from '@/views/Loyalty/LoyaltyRegister.vue';

// Wash
import ProfileWash from './views/Profile/ProfileWash.vue';
import ProfileWashDetails from './views/Profile/ProfileWashDetails.vue';
// Cars
import Cars from './views/Cars/Cars.vue';
import CarDetails from './views/Cars/CarDetails.vue';

import Login from './views/Login/Login.vue';
import ForgottenPassword from './views/Login/ForgottenPassword.vue';

import { useAuthStore } from '@/store/auth/auth';


const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/vouchers',
      component: VoucherList,
      meta: { title: 'Ryd Pay: vouchers' }
    },
    {
      path: '/vouchers/add',
      component: VoucherCustomAdd,
      meta: { title: 'Ryd Pay: vouchers add' }
    },
    {
      path: '/loyalty',
      component: Loyalty,
      meta: { title: 'Ryd Pay: loyalties' }
    },
    {
      path: '/loyalty/add',
      component: LoyaltyAdd,
      meta: { title: 'Ryd Pay: loyalty add' }
    },
    {
      path: '/profile/loyalty/register',
      component: LoyaltyRegister,
      meta: { title: 'Ryd Pay: loyalty' }
    },
    {
      path: '/history',
      component: History,
      meta: { requiresAuth: true, requiresAllThing: true, title: 'Ryd Pay: history' }
    },
    {
      path: '/history/create',
      component: HistoryCreate,
      meta: { requiresAuth: true, requiresAllThing: true, title: 'Ryd Pay: history create' }
    },
    {
      path: '/history/:id',
      component: HistoryDetails,
      meta: { requiresAuth: true, requiresAllThing: true, title: 'Ryd Pay: history details' }
    },
    {
      path: '/history/:id/merge',
      component: MergeTrip,
      meta: { requiresAuth: true, requiresAllThing: true }
    },
    {
      path: '/history/transactions-overview',
      component: TransactionsOverview,
      meta: { requiresAllThing: true, title: 'Ryd Pay: transactions overview' }
    },
    {
      path: '/profile/wash',
      component: ProfileWash,
      meta: { requiresAllThing: true, title: 'Ryd Pay: wash tickets' }
    },
    {
      path: '/profile/wash/:id',
      component: ProfileWashDetails,
      meta: { requiresAllThing: true, title: 'Ryd Pay: wash ticket details' }
    },
    {
      path: '/cars',
      component: Cars,
      meta: { requiresAuth: true, requiresAllThing: true, title: 'Ryd Pay: your vehicles', hasNewUi: true }
    },
    {
      path: '/cars/:id',
      component: CarDetails,
      meta: { requiresAuth: true, requiresUserId: true, requiresAllThing: true, title: 'Ryd Pay: vehicle details', hasNewUi: true }
    },
    {
      path: '/beta-test',
      component: () => import('./views/BetaTest/BetaTest.vue'),
      meta: { requiresAuth: true, requiresUserId: true, title: 'Ryd Pay: beta test', hasNewUi: true }
    },
    {
      path: '/login',
      component: Login,
      meta: { title: 'Automotive Ryd Login', hasNewUi: true }
    },
    {
      path: '/register',
      component: () => import('./views/Login/Register.vue'),
      meta: { title: 'Automotive Ryd Register', hasNewUi: true }
    },
    {
      path: '/change-tmp-password',
      component: () => import('./views/Login/ChangeTmpPassword.vue'),
      meta: { title: 'Automotive Ryd Register', hasNewUi: true }
    },
    {
      path: '/forgotten-password',
      component: ForgottenPassword,
      meta: { title: 'Ryd Pay: forgot password', hasNewUi: true }
    },
    {
      path: '/tutorials',
      component: () => import('./views/Tutorial/Tutorials.vue'),
      meta: { requiresAuth: true, title: 'Ryd Pay: tutorials', hasNewUi: true }
    },
    {
      path: '/tutorials/:id',
      component: () => import('./views/Tutorial/Tutorial.vue'),
      meta: { requiresAuth: true, title: 'Ryd Pay: tutorial', hasNewUi: true }
    },
    {
      path: '/partner-wallet',
      component: () => import('./views/PartnerWallet/PartnerWalletEntry.vue'),
      children: [
        {
          path: '',
          component: () => import('./views/PartnerWallet/PartnerWallet.vue'),
        },
        {
          path: '/partner-wallet/add/:id',
          component: () => import('./views/PartnerWallet/PartnerWalletAdd.vue')
        },
        {
          path: '/partner-wallet/:id',
          component: () => import('./views/PartnerWallet/PartnerWalletDetails.vue'),
        },
      ]
    },
    {
      path: '/maintenance',
      component: Maintenance
    },
    {
      path: '/error',
      component: Error,
      meta: { title: 'Ryd Pay: error page' }
    },
    {
      path: '/:pathMatch(.*)',
      component: NotFound,
      meta: { title: 'Ryd Pay: page not found' }
    }
  ]
});

import { setLanguage } from './i18n.js';
import { checkAndApplyTheme, removeTheme } from '@/utils/utils';

const hasQueryParams = (route: RouteLocationNormalized) => {
  return !!Object.keys(route.query).length;
};
type URLParamsType = {
  token: string;
  account: string;
  lang: string;
  userId: string;
  email: string;
  currency: string;
};
function filterParams(route: RouteLocationNormalized, exclude = ['token']) {
  const url = new URL(window.location.origin + route.fullPath);
  exclude.forEach(param => url.searchParams.delete(param));
  return url.pathname + url.search;
}

router.beforeEach(async (to, from, next) => {
  window.scrollTo(0, 0);
  window.document.title = to.meta?.title ? `${to.meta.title}` : 'Ryd Pay';
  const authState = useAuthStore();

  const URLparams = to.query as URLParamsType;
  const tokenFromWindow = window.RydAuth ? window.RydAuth.getAuthToken() : null;

  const token = tokenFromWindow || URLparams.token || from.query.token;

  // Save the token, is always needed
  if (!authState.authToken || authState.authToken !== token) {
    await authState.saveToken((token as string), URLparams.lang);
    await setLanguage(authState.lang);
  }

  // Page requires thing ID
  if (to.matched.some(record => record.meta.requiresAllThing)) {
    if (!authState.userPreferences?.length) await authState.storeUserPreferences();

    const thingFromQuery = to.query.thingId || from.query.thingId;

    if (!authState.preferredVehicle) {
      await authState.setPreferredVehicle(thingFromQuery as string);
    }
  }

  if (to.matched.some(record => record.meta.requiresUserId)) {
    if (authState.userId !== from.query.userId && !authState.userId) {
      await authState.getUserId();
    }
  }

  // Check requiresAuth
  if (to.matched.some(record => record.meta.requiresAuth)) {

    // We save the URL params into store if the auth store is empty or if there's a new token in URL
    if (!authState.authToken || authState.authToken !== token) {

      try {
        authState.isLoading = true;

        await authState.saveAuthData(URLparams.userId, URLparams.account, URLparams.email, URLparams.currency);

      } catch (error) {
        console.log('router auth error', error);
      } finally {
        authState.isLoading = false;

      }
    }
  }

  if (to.matched.some(record => record.meta.hasNewUi)) {
    checkAndApplyTheme();
  } else {
    removeTheme();
  }
  // Firebase page view tracking
  if (analytics) {
    logEvent((analytics as Analytics), 'page_view' as any, {
      page_title: to.meta?.title || 'Ryd Pay',
      page_location: window.location.href,
      page_path: filterParams(to, ['token'])
    });
  }

  // We persist the parameters through all the pages
  if (!hasQueryParams(to) && hasQueryParams(from)) {
    next({ path: to.path, query: from.query });
  } else {
    next();
  }
});

export default router;
